import React, {useState, useEffect} from "react";
import moment from "moment";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {GetFlaggedService} from "../../services/contractorServices";
import {issueLabels} from "../../constant";

import Alert from "@mui/material/Alert";

const MyFlagged = () => {
	const [loading, setLoading] = useState(true);
	const [flaggedData, setFlaggedData] = useState(null);
	const [activeTab, setActiveTab] = useState("all");
	const [searchTerm, setSearchTerm] = useState("");
	const [sortBy, setSortBy] = useState("newest"); // newest, oldest

	// Fetch flagged data from API
	const fetchFlaggedData = async () => {
		try {
			setLoading(true);
			const response = await GetFlaggedService();

			if (response && response.status === true && response.data) {
				setFlaggedData(response.data);
			} else {
				setFlaggedData(null);
			}
		} catch (error) {
			console.error("Error fetching flagged data:", error);
			setFlaggedData(null);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchFlaggedData();
	}, []);

	const handleTabChange = (tab) => {
		setActiveTab(tab);
		setSearchTerm(""); // Clear search when switching tabs
	};

	const handleSortChange = (sort) => {
		setSortBy(sort);
	};

	const renderStars = (rating) => {
		return [...Array(5)].map((_, index) => (
			<span key={index} className={`star ${index < rating ? "filled" : ""}`}>
				★
			</span>
		));
	};

	const getRatingColor = (rating) => {
		if (rating >= 4) return "rating-excellent";
		if (rating >= 3) return "rating-good";
		if (rating >= 2) return "rating-fair";
		return "rating-poor";
	};

	const getStatusBadge = (isHistoric, adminFlagged) => {
		if (adminFlagged) {
			return <span className="badge badge-danger">Flagged</span>;
		}
		return isHistoric ? <span className="badge badge-warning">Historic</span> : <span className="badge badge-success">Fresh</span>;
	};

	const getCommentTypeBadge = (commentType) => {
		return commentType ? <span>Zip code</span> : <span>Street</span>;
	};

	// Sort data based on sortBy
	const sortData = (data) => {
		if (!data || data.length === 0) return data;

		return [...data].sort((a, b) => {
			let dateA, dateB;

			// Get the appropriate date based on item type with null checks
			if (a.itemType === "rating") {
				dateA = a.job_date ? moment(a.job_date) : moment(0); // Use epoch if no date
			} else if (a.itemType === "comment") {
				dateA = a.comment_date ? moment(a.comment_date) : moment(0);
			} else if (a.itemType === "address") {
				dateA = a.createdAt ? moment(a.createdAt) : moment(0);
			} else {
				// For individual tabs without itemType
				if (activeTab === "ratings") {
					dateA = a.job_date ? moment(a.job_date) : moment(0);
				} else if (activeTab === "comments") {
					dateA = a.comment_date ? moment(a.comment_date) : moment(0);
				} else if (activeTab === "addresses") {
					dateA = a.createdAt ? moment(a.createdAt) : moment(0);
				} else {
					dateA = moment(0);
				}
			}

			if (b.itemType === "rating") {
				dateB = b.job_date ? moment(b.job_date) : moment(0);
			} else if (b.itemType === "comment") {
				dateB = b.comment_date ? moment(b.comment_date) : moment(0);
			} else if (b.itemType === "address") {
				dateB = b.createdAt ? moment(b.createdAt) : moment(0);
			} else {
				// For individual tabs without itemType
				if (activeTab === "ratings") {
					dateB = b.job_date ? moment(b.job_date) : moment(0);
				} else if (activeTab === "comments") {
					dateB = b.comment_date ? moment(b.comment_date) : moment(0);
				} else if (activeTab === "addresses") {
					dateB = b.createdAt ? moment(b.createdAt) : moment(0);
				} else {
					dateB = moment(0);
				}
			}

			// Check if dates are valid
			if (!dateA.isValid()) dateA = moment(0);
			if (!dateB.isValid()) dateB = moment(0);

			if (sortBy === "newest") {
				return dateB.diff(dateA); // Newest first
			} else {
				return dateA.diff(dateB); // Oldest first
			}
		});
	};

	// Filter data based on search
	const getFilteredData = () => {
		if (!flaggedData) return [];

		let filteredData = [];

		switch (activeTab) {
			case "ratings":
				filteredData =
					flaggedData.ratings?.filter((item) => {
						const address = item.address?.full_address || "";
						const jobIssues = Array.isArray(item.job_issues) ? item.job_issues.join(" ") : "";
						return searchTerm === "" || address.toLowerCase().includes(searchTerm.toLowerCase()) || jobIssues.toLowerCase().includes(searchTerm.toLowerCase());
					}) || [];
				break;
			case "comments":
				filteredData =
					flaggedData.street_comments?.filter((item) => {
						const address = item.addresses || "";
						const description = item.description || "";
						return searchTerm === "" || address.toLowerCase().includes(searchTerm.toLowerCase()) || description.toLowerCase().includes(searchTerm.toLowerCase());
					}) || [];
				break;
			case "addresses":
				filteredData =
					flaggedData.addresses?.filter((item) => {
						const address = item.full_address || "";
						return searchTerm === "" || address.toLowerCase().includes(searchTerm.toLowerCase());
					}) || [];
				break;
			case "all":
				// Combine all data for "all" filter - MIXED together
				const allData = [];

				// Add ratings with type indicator
				if (flaggedData.ratings) {
					flaggedData.ratings.forEach((item) => {
						allData.push({...item, itemType: "rating"});
					});
				}

				// Add comments with type indicator
				if (flaggedData.street_comments) {
					flaggedData.street_comments.forEach((item) => {
						allData.push({...item, itemType: "comment"});
					});
				}

				// Add addresses with type indicator
				if (flaggedData.addresses) {
					flaggedData.addresses.forEach((item) => {
						allData.push({...item, itemType: "address"});
					});
				}

				filteredData = allData;
				break;
			default:
				filteredData = [];
		}

		// Apply sorting
		return sortData(filteredData);
	};

	const filteredData = getFilteredData();

	// Calculate stats
	const totalRatings = flaggedData?.ratings?.length || 0;
	const totalComments = flaggedData?.street_comments?.length || 0;
	const totalAddresses = flaggedData?.addresses?.length || 0;
	const totalAll = totalRatings + totalComments + totalAddresses;

	return (
		<div className="dashboard-page">
			<div className="page-header">
				<h2>My Flagged Content</h2>
				<p>View and manage all your flagged ratings, comments, and addresses</p>
			</div>

			{/* Stats Cards */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-icon stat-icon-primary">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" />
						</svg>
					</div>
					<div className="stat-content">
						<div className="stat-number">{totalAll}</div>
						<div className="stat-label">Total Items</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-icon stat-icon-danger">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" />
						</svg>
					</div>
					<div className="stat-content">
						<div className="stat-number">{totalRatings}</div>
						<div className="stat-label">Flagged Ratings</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-icon stat-icon-warning">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" />
						</svg>
					</div>
					<div className="stat-content">
						<div className="stat-number">{totalComments}</div>
						<div className="stat-label">Street Comments</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-icon stat-icon-info">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2" />
							<circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
						</svg>
					</div>
					<div className="stat-content">
						<div className="stat-number">{totalAddresses}</div>
						<div className="stat-label">Total Addresses</div>
					</div>
				</div>
			</div>

			{/* Filter and Sort Controls */}
			<div className="ratings-controls ">
				<div className="filter-section">
					<select className="filter-select" value={activeTab} onChange={(e) => handleTabChange(e.target.value)}>
						<option value="all">All Items ({totalAll})</option>
						<option value="ratings">Ratings ({totalRatings})</option>
						<option value="comments">Comments ({totalComments})</option>
						<option value="addresses">Addresses ({totalAddresses})</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
						<option value="newest">Newest First</option>
						<option value="oldest">Oldest First</option>
					</select>
				</div>
			</div>

			{/* Content List */}
			{loading ? (
				<div className="ratings-container">
					<CustomLoader />
				</div>
			) : (
				<div className="ratings-container">
					{filteredData.length === 0 ? (
						<div className="no-ratings">
							<div className="placeholder-icon">
								<svg width="80" height="80" viewBox="0 0 24 24" fill="none">
									<path
										d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
										stroke="currentColor"
										strokeWidth="1.5"
									/>
								</svg>
							</div>
							<h3>No {activeTab} found</h3>
							<p>{searchTerm ? "Try adjusting your search criteria" : `No ${activeTab} available`}</p>
						</div>
					) : (
						<>
							<Alert sx={{mb: 3}} severity="error">
								This rating/comment has been flagged by the admin. Please contact the admin for clarification.
							</Alert>
							
							<div className="ratings-grid">
								{/* Mixed View - All Items Together */}
								{activeTab === "all" &&
									filteredData.map((item, index) => (
										<div key={`${item.itemType}-${item.rating_id || item.street_id || item.addresses_id || index}`} className={`rating-card ${item.itemType === "" ? "" : ""}`}>
											<div className="rating-card-header">
												<div className="address-info">
													<h4 className={item.itemType === "comment" ? "comment-address" : "job-address"}>
														{item.itemType === "rating" ? item.address?.full_address || "N/A" : item.itemType === "comment" ? item.addresses : item.full_address}
													</h4>
													<div className="rating-info">
														<div className="item-type-badge">
															{item.itemType === "rating" && (
																<>
																	<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																		<path
																			d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
																			stroke="currentColor"
																			strokeWidth="2"
																		/>
																	</svg>
																	<span>Flagged Rating</span>
																</>
															)}
															{item.itemType === "comment" && (
																<>
																	<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																		<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" />
																	</svg>
																	<span>Street Comment</span>
																</>
															)}
															{item.itemType === "address" && (
																<>
																	<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																		<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2" />
																		<circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
																	</svg>
																	<span>Address</span>
																</>
															)}
														</div>
														<span className="comment-date">
															{item.itemType === "rating"
																? item.job_date
																	? moment(item.job_date).format("MMM DD, YYYY")
																	: "N/A"
																: item.itemType === "comment"
																? item.comment_date
																	? moment(item.comment_date).format("MMM DD, YYYY")
																	: "N/A"
																: item.createdAt
																? moment(item.createdAt).format("MMM DD, YYYY")
																: "N/A"}
														</span>
													</div>
												</div>
											</div>

											<div className="rating-card-body">
												{/* Rating Content */}
												{item.itemType === "rating" && (
													<>
														<div className="rating-display">
															<div className={`rating-stars ${getRatingColor(item.stars)}`}>
																{renderStars(item.stars)}
																<span className="rating-number">({item.stars}/5)</span>
															</div>
															{item.job_issues && item.job_issues.length > 0 && (
																<div className="issues-list">
																	{item.job_issues.slice(0, 2).map((issue, issueIndex) => (
																		<span key={issueIndex} className="issue-tag">
																			{issueLabels[issue] || issue.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
																		</span>
																	))}
																	{item.job_issues.length > 2 && <span className="issue-tag more">+{item.job_issues.length - 2} more</span>}
																</div>
															)}
														</div>
														<div className="job-details">
															<div className="detail-item">
																<span className="detail-label">Job Date:</span>
																<span className="detail-value">{item.job_date ? moment(item.job_date).format("MMM DD, YYYY") : "N/A"}</span>
																{item.job_date && <span className="days-ago">({moment().diff(moment(item.job_date), "days")} days ago)</span>}
															</div>
															<div className="detail-item">
																{/* <span className="detail-label">Status:</span> */}
																{/* <span className="detail-value">{getStatusBadge(item.is_historic, item.admin_flagged)}</span> */}
															</div>
														</div>
													</>
												)}

												{/* Comment Content */}
												{item.itemType === "comment" && (
													<>
														<div className="comment-description">
															<p>{item.description ? (item.description.length > 120 ? `${item.description.substring(0, 120)}...` : item.description) : "No description"}</p>
														</div>
														<div className="job-details">
															<div className="detail-item">
																<span className="detail-label">Comment Date:</span>
																<span className="detail-value">{item.comment_date ? moment(item.comment_date).format("MMM DD, YYYY") : "N/A"}</span>
																{item.comment_date && <span className="days-ago">({moment().diff(moment(item.comment_date), "days")} days ago)</span>}
															</div>
															<div className="detail-item">
																<span className="detail-label">Type:</span>
																<span className="detail-value">{getCommentTypeBadge(item.comment_type)}</span>
															</div>
														</div>
													</>
												)}

												{/* Address Content */}
												{item.itemType === "address" && (
													<div className="job-details">
														<div className="detail-item">
															<span className="detail-label">Created:</span>
															<span className="detail-value">{item.createdAt ? moment(item.createdAt).format("MMM DD, YYYY") : "N/A"}</span>
															{item.createdAt && <span className="days-ago">({moment().diff(moment(item.createdAt), "days")} days ago)</span>}
														</div>
													</div>
												)}
											</div>
										</div>
									))}

								{/* Individual Tab Views - Keep these for when specific tabs are selected */}
								{activeTab === "ratings" &&
									filteredData.map((item) => (
										<div key={item.rating_id} className="rating-card ">
											<div className="rating-card-header">
												<div className="address-info">
													<h4 className="job-address">{item.address?.full_address || "N/A"}</h4>
													<div className="rating-info">
														<div className="item-type-badge">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path
																	d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
																	stroke="currentColor"
																	strokeWidth="2"
																/>
															</svg>
															<span>Flagged Rating</span>
														</div>
														<span className="comment-date">{item.job_date ? moment(item.job_date).format("MMM DD, YYYY") : "N/A"}</span>
													</div>
												</div>
											</div>

											<div className="rating-card-body">
												<div className="rating-display">
													<div className={`rating-stars ${getRatingColor(item.stars)}`}>
														{renderStars(item.stars)}
														<span className="rating-number">({item.stars}/5)</span>
													</div>
													{item.job_issues && item.job_issues.length > 0 && (
														<div className="issues-list">
															{item.job_issues.slice(0, 2).map((issue, index) => (
																<span key={index} className="issue-tag">
																	{issueLabels[issue] || issue.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
																</span>
															))}
															{item.job_issues.length > 2 && <span className="issue-tag more">+{item.job_issues.length - 2} more</span>}
														</div>
													)}
												</div>

												<div className="job-details">
													<div className="detail-item">
														<span className="detail-label">Job Date:</span>
														<span className="detail-value">{item.job_date ? moment(item.job_date).format("MMM DD, YYYY") : "N/A"}</span>
														{item.job_date && <span className="days-ago">({moment().diff(moment(item.job_date), "days")} days ago)</span>}
													</div>
													{/* <div className="detail-item">
													<span className="detail-label">Status:</span>
													<span className="detail-value">{getStatusBadge(item.is_historic, item.admin_flagged)}</span>
												</div> */}
												</div>
											</div>
										</div>
									))}

								{activeTab === "comments" &&
									filteredData.map((item) => (
										<div key={item.street_id} className="rating-card ">
											<div className="rating-card-header">
												<div className="address-info">
													<h4 className="comment-address">{item.addresses}</h4>
													<div className="rating-info">
														<div className="item-type-badge">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" />
															</svg>
															<span>Street Comment</span>
														</div>
														<span className="comment-date">{item.comment_date ? moment(item.comment_date).format("MMM DD, YYYY") : "N/A"}</span>
													</div>
												</div>
											</div>

											<div className="rating-card-body">
												<div className="comment-description">
													<p>{item.description ? (item.description.length > 120 ? `${item.description.substring(0, 120)}...` : item.description) : "No description"}</p>
												</div>

												<div className="job-details">
													<div className="detail-item">
														<span className="detail-label">Comment Date:</span>
														<span className="detail-value">{item.comment_date ? moment(item.comment_date).format("MMM DD, YYYY") : "N/A"}</span>
														{item.comment_date && <span className="days-ago">({moment().diff(moment(item.comment_date), "days")} days ago)</span>}
													</div>
													<div className="detail-item">
														<span className="detail-label">Type:</span>
														<span className="detail-value">{getCommentTypeBadge(item.comment_type)}</span>
													</div>
												</div>
											</div>
										</div>
									))}

								{activeTab === "addresses" &&
									filteredData.map((item) => (
										<div key={item.addresses_id} className="rating-card">
											<div className="rating-card-header">
												<div className="address-info">
													<h4 className="job-address">{item.full_address}</h4>
													<div className="rating-info">
														<div className="item-type-badge">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2" />
																<circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
															</svg>
															<span>Address</span>
														</div>
														<span className="comment-date">{item.createdAt ? moment(item.createdAt).format("MMM DD, YYYY") : "N/A"}</span>
													</div>
												</div>
											</div>

											<div className="rating-card-body">
												<div className="job-details">
													<div className="detail-item">
														<span className="detail-label">Created:</span>
														<span className="detail-value">{item.createdAt ? moment(item.createdAt).format("MMM DD, YYYY") : "N/A"}</span>
														{item.createdAt && <span className="days-ago">({moment().diff(moment(item.createdAt), "days")} days ago)</span>}
													</div>
												</div>
											</div>
										</div>
									))}
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default MyFlagged;
